import { useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

export default function LoginModal() {
  const { loginOpen, setLoginOpen, signInWithGoogle } = useAuth();

  useEffect(() => {
    if (loginOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [loginOpen]);

  const handleGoogleLogin = async () => {
    try {
      await signInWithGoogle();
    } catch (e) {
      toast.error(e?.message || "Google sign-in failed");
    }
  };

  return (
    <AnimatePresence>
      {loginOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLoginOpen(false)}
          data-testid="login-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white w-full max-w-md grid"
          >
            <button
              onClick={() => setLoginOpen(false)}
              className="absolute top-4 right-4 z-10 hover:opacity-60"
              aria-label="Close"
              data-testid="login-modal-close"
            >
              <X size={20} />
            </button>

            {/* Header strip */}
            <div className="bg-[#0A0A0A] text-white p-8 pb-10">
              <div className="label-eyebrow text-[#C9A961] mb-3">Members · House of Crown</div>
              <h2 className="font-display text-3xl md:text-4xl tracking-tighter leading-[0.95]">
                Continue with<br />
                <span className="italic font-light text-[#C9A961]">your Google.</span>
              </h2>
              <p className="mt-5 text-sm text-white/60">
                One tap. No password. Your bag and orders stay with you.
              </p>
            </div>

            {/* Action */}
            <div className="p-8">
              <button
                onClick={handleGoogleLogin}
                data-testid="google-login-btn"
                className="w-full bg-white border border-black h-14 flex items-center justify-center gap-3 text-sm font-medium tracking-wide hover:bg-black hover:text-white transition-colors"
              >
                <GoogleIcon />
                Continue with Google
              </button>

              <div className="mt-6 space-y-2 text-xs text-black/50 leading-relaxed">
                <p>· No passwords to remember.</p>
                <p>· Your bag, addresses &amp; order history are saved securely.</p>
                <p>· Sign out any time.</p>
              </div>

              <div className="mt-8 pt-6 border-t border-black/10 text-[11px] text-black/40">
                By continuing, you agree to our terms and acknowledge our privacy policy.
                We store only your Google ID, email, and name.
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );
}
